import { useQuery } from '@tanstack/react-query';
import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import { useAuth } from './useAuth';
import { useImpersonation } from '../context/useImpersonation';

export const practiceFeatureFlagKeys = {
  flags: (practiceId: string) => ['practice', 'featureFlags', practiceId] as const,
};

interface PracticeWithFlags {
  id: string;
  featureFlags?: Record<string, unknown> | null;
}

/**
 * The current practice's `featureFlags` registry (Command Center Admin tab).
 * Follows impersonation automatically: the API client sends X-Practice-Id, so
 * an agency admin viewing as a practice sees that practice's flags.
 */
export function usePracticeFeatureFlags() {
  const { user } = useAuth();
  const { impersonatedPractice } = useImpersonation();
  const practiceId = impersonatedPractice?.id ?? user?.practiceId ?? '';

  const query = useQuery({
    queryKey: practiceFeatureFlagKeys.flags(practiceId),
    queryFn: () =>
      apiClient.get<ApiResponse<PracticeWithFlags>>('/practice').then((r) => {
        const raw = r.data.data.featureFlags ?? {};
        const flags: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(raw)) if (typeof v === 'boolean') flags[k] = v;
        return flags;
      }),
    enabled: !!practiceId,
    staleTime: 60_000,
  });

  return { flags: query.data ?? null, isLoading: !!practiceId && query.isLoading, practiceId };
}
